// =============================================================
// Charts — dependency-free SVG/CSS primitives ported from the hi-fi prototype
// (scripts/perpetuum-desktop-sections2.jsx: spark / .bars / .donut).
//
//   • Sparkline — small inline trend polyline (SVG).
//   • BarChart  — vertical stacked bars + legend (planned vs unplanned style).
//   • Donut     — conic-gradient ring with a centre label + legend.
//
// All generic + typed; data comes in via props. Colour defaults to design-system
// status/brand tokens but each series may override with any CSS colour string.
// Accessibility: charts expose role="img" + an aria-label summarising the data.
// =============================================================

import type { CSSProperties } from "react";

// ---------------------------------------------------------------------------
// Sparkline
// ---------------------------------------------------------------------------

export interface SparklineProps {
  /** Ordered numeric series. Needs ≥ 2 points to draw a line. */
  points: number[];
  /** Stroke colour (any CSS colour). Defaults to the brand fill. */
  color?: string;
  width?: number;
  height?: number;
  /** Accessible summary; falls back to a generic trend label. */
  ariaLabel?: string;
}

/** Small inline trend line (no axes). */
export function Sparkline({ points, color = "var(--brand-primary-fill)", width = 120, height = 34, ariaLabel }: SparklineProps) {
  const w = width;
  const h = height;
  if (points.length < 2) {
    return <svg className="spark" viewBox={`0 0 ${w} ${h}`} role="img" aria-label={ariaLabel ?? "No trend data"} />;
  }
  const max = Math.max(...points);
  const min = Math.min(...points);
  const span = max - min || 1;
  const d = points
    .map((p, i) => `${(i / (points.length - 1)) * w},${h - 2 - ((p - min) / span) * (h - 6)}`)
    .join(" ");
  return (
    <svg
      className="spark"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={ariaLabel ?? `Trend from ${points[0]} to ${points[points.length - 1]}`}
    >
      <polyline points={d} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// BarChart — vertical bars, optionally stacked, with a legend.
// ---------------------------------------------------------------------------

/** One stacked segment of a bar (value + colour). */
export interface BarSegment {
  value: number;
  /** Colour for this segment (any CSS colour). */
  color?: string;
  /** Legend / tooltip label for this series. */
  label?: string;
}

export interface BarChartBar {
  /** X-axis tick label (e.g. a week or category name). */
  label: string;
  /** One or more stacked segments. */
  segments: BarSegment[];
}

export interface BarChartProps {
  bars: BarChartBar[];
  /** Optional fixed max for the Y scale; otherwise the tallest bar total. */
  max?: number;
  /** Legend entries (series). Omit to hide the legend. */
  legend?: { label: string; color: string }[];
  /** Unit suffix for tooltips/aria (e.g. "min", "h"). */
  unit?: string;
  ariaLabel?: string;
  height?: number;
}

const DEFAULT_BAR_COLOR = "var(--brand-primary-fill)";

/** Vertical (stacked) bar chart with an optional legend. */
export function BarChart({ bars, max, legend, unit = "", ariaLabel, height = 180 }: BarChartProps) {
  const totals = bars.map((b) => b.segments.reduce((s, seg) => s + seg.value, 0));
  const scaleMax = Math.max(1, max ?? Math.max(0, ...totals));
  const summary =
    ariaLabel ?? `Bar chart: ${bars.map((b, i) => `${b.label} ${totals[i]}${unit}`).join(", ") || "no data"}`;

  return (
    <div className="bc">
      {legend && legend.length > 0 && (
        <div className="bc-legend">
          {legend.map((l) => (
            <span key={l.label}>
              <span className="bc-sw" style={{ background: l.color }} aria-hidden="true" />
              {l.label}
            </span>
          ))}
        </div>
      )}
      <div className="bc-bars" role="img" aria-label={summary} style={{ height }}>
        {bars.map((b, i) => {
          const total = totals[i] ?? 0;
          return (
            <div className="bc-col" key={`${b.label}-${i}`}>
              <div className="bc-stack" style={{ height: `${(total / scaleMax) * 100}%` }}>
                {b.segments.map((seg, si) => {
                  const segStyle: CSSProperties = {
                    height: total > 0 ? `${(seg.value / total) * 100}%` : "0%",
                    background: seg.color ?? DEFAULT_BAR_COLOR,
                  };
                  return (
                    <div
                      key={si}
                      className="bc-seg"
                      style={segStyle}
                      title={`${seg.label ? `${seg.label}: ` : ""}${seg.value}${unit}`}
                    />
                  );
                })}
              </div>
              <span className="bc-x">{b.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Donut — conic-gradient ring with a centre label + legend.
// ---------------------------------------------------------------------------

export interface DonutSlice {
  label: string;
  value: number;
  /** Slice colour (any CSS colour). */
  color: string;
  /** Optional pre-formatted value for the legend (else the raw value). */
  display?: string;
}

export interface DonutProps {
  slices: DonutSlice[];
  /** Big centre number (already formatted). */
  centerValue?: string;
  /** Small centre caption under the value. */
  centerLabel?: string;
  /** Hide the legend column. */
  hideLegend?: boolean;
  size?: number;
  ariaLabel?: string;
}

/** Conic-gradient donut with a centre label and a slice legend. */
export function Donut({ slices, centerValue, centerLabel, hideLegend = false, size = 160, ariaLabel }: DonutProps) {
  const total = slices.reduce((s, x) => s + x.value, 0);
  // Build conic-gradient stops from cumulative percentages.
  let acc = 0;
  const stops = slices
    .map((s) => {
      const start = total > 0 ? (acc / total) * 100 : 0;
      acc += s.value;
      const end = total > 0 ? (acc / total) * 100 : 0;
      return `${s.color} ${start}% ${end}%`;
    })
    .join(", ");
  const gradient = total > 0 ? `conic-gradient(${stops})` : "var(--sys-bg-sunken)";
  const summary =
    ariaLabel ?? `Donut chart: ${slices.map((s) => `${s.label} ${s.display ?? s.value}`).join(", ") || "no data"}`;

  return (
    <div className="donut-wrap">
      <div
        className="donut"
        role="img"
        aria-label={summary}
        style={{ background: gradient, width: size, height: size }}
      >
        <div className="donut-hole">
          {centerValue != null && <span className="donut-big">{centerValue}</span>}
          {centerLabel != null && <span className="donut-lbl">{centerLabel}</span>}
        </div>
      </div>
      {!hideLegend && (
        <div className="donut-legend">
          {slices.map((s) => (
            <div className="donut-row" key={s.label}>
              <span className="bc-sw" style={{ background: s.color }} aria-hidden="true" />
              {s.label}
              <span className="donut-val">{s.display ?? s.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
